/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CurateSearchVm } from '../../models/curate-search-vm';
import { ResultInformation } from '../../models/result-information';
import { LocalStorageService } from "../../../../../utils/src/lib/local-storage/local-storage.service";
import {
  IResultContainerFacade,
  RESULT_CONTAINER_FACADE_TOKEN,
} from '../../../../../../../sysque/toolbelt/data-access/src/lib/domain/facades-models/result-container-facade-interface';
import { GlobalConstants } from '../../constants/global-constants';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { ResultItemInformation } from '../../models/result-item-information';
import { AddToSystemReturnCodes } from '../../models/add-to-system-return-codes';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { ImageDescriptionType } from '../../../../../../shared/ui/src/lib/enums/image-description-type';
import { SysQueStateService } from '../../../../../utils/src/lib/sysque-electrical-state/sysque-elecrical-state.service';

@Component({
  selector: 'sysque-result-container',
  templateUrl: './result-container.component.html',
  styleUrls: ['./result-container.component.scss'],
  standalone: false,
})
export class ResultContainerComponent implements OnInit, OnDestroy {
  public resultsFromCurate: ResultInformation[] = [];
  resultInformation: ResultInformation[] = [];
  resultInformationSub$: Subscription;
  treeViewResultInformationSub$: Subscription;
  lastCurateSearchQuery: CurateSearchVm;
  addFittingState = new SysqueElectricalState();
  selectedTab: string;
  value = '0';
  visibility = false;
  item: Item = {
    descriptionInstance: '',
    descriptionType: '',
    id: '',
    isDefault: false,
    displayPosition: '',
    image: '',
    isDisabled: false,
    tooltipItems: [],
    name: '',
    fittingType: '',
    connectors: '',
    shape: '',
    lengthTooltip: '',
    manufacturer: '',
    sizeRange: '',
    serviceType: '',
    serviceTypeColor: '',
    bendRadius: '',
    leadLength: '',
    angle: '',
    category: '',
    unit: '',
    spacing: '',
    distFromFittings: '',
    distFromCoupling: '',
    distFromBeam: '',
    diameter: '',
    depth: '',
    width: '',
    errorHangerRodLength: '',
    errorHangerColor: '',
    rodDiameter: '',
    noOverride: false,
    itemType: '',
    isEditViewSettings: false,
    isHanger: '',
    isSysQueConnectOn: false,
    offsetType: '',
    bendType: '',
    defaultFor: '',
    position: '',
    specId: '',
    imageDescriptionType: ImageDescriptionType.INN,
    itemPath: '',
    centerRadius: '',
    height: '',
  };
  errorMap = new Map<number, string>([
    [AddToSystemReturnCodes.BackupFamily, GlobalConstants.BACKUP_FAMILY],
    [AddToSystemReturnCodes.NotMetricFamily, GlobalConstants.NOT_METRIC_FAMILY],
    [AddToSystemReturnCodes.NotImperialFamily, GlobalConstants.NOT_IMPERIAL_FAMILY],
    [AddToSystemReturnCodes.FileNotAvailable, GlobalConstants.FILE_NOT_AVAILABLE],
    [AddToSystemReturnCodes.InternetConnection, GlobalConstants.INTERNET_CONNECTION],
    [AddToSystemReturnCodes.AlreadyExist, GlobalConstants.ALREADY_EXIST],
    [AddToSystemReturnCodes.BuildingLayoutTab, GlobalConstants.BUILDING_LAYOUT_TAB],
    [AddToSystemReturnCodes.AccessoriesCustomContentTab, GlobalConstants.ACCESORIES_CUSTOM_CONTENT_TAB],
    [AddToSystemReturnCodes.AssembliesTab, GlobalConstants.ASSEMBLIES_TAB],
    [AddToSystemReturnCodes.CustomContentTab, GlobalConstants.CUSTOM_CONTENT_TAB],
    [AddToSystemReturnCodes.AccessoriesTab, GlobalConstants.ACCESORIES_TAB],
    [AddToSystemReturnCodes.ConduitFieldFabTab, GlobalConstants.CONDUIT_FIELD_FAB_TAB],
    [AddToSystemReturnCodes.LadderCableTrayTab, GlobalConstants.LADDER_CABLE_TRAY_TAB],
    [AddToSystemReturnCodes.ClearanceFittings, GlobalConstants.CLEARANCE_FITTINGS],
    [AddToSystemReturnCodes.ChannelCableTray, GlobalConstants.CHANNEL_CABLE_TRAY],
    [AddToSystemReturnCodes.WireMeshCableTray, GlobalConstants.WIRE_MESH_CABLE_TRAY],
    [AddToSystemReturnCodes.FamilyNotFound, GlobalConstants.FAMILY_NOT_FOUND],
    [AddToSystemReturnCodes.UpdatedFamilies, GlobalConstants.UPDATED_FAMILIES],
    [AddToSystemReturnCodes.Error, GlobalConstants.ERROR],
    [AddToSystemReturnCodes.WrongTab, GlobalConstants.WRONG_TAB],
    [AddToSystemReturnCodes.JunctionBox, GlobalConstants.JUNCTION_BOX],
    [AddToSystemReturnCodes.BuildingLayoutFamily, GlobalConstants.BUILDING_LAYOUT_FAMILY],
  ]);
  component: {
    descriptionInstance: string;
    descriptionType: string;
    id: string;
    isDefault: false;
    displayPosition: string;
    image: string;
    isDisabled: false;
    tooltipItems: any[];
    name: string;
    fittingType: string;
    connectors: string;
    shape: string;
    lengthTooltip: string;
    manufacturer: string;
    sizeRange: string;
    serviceType: string;
    serviceColor: string;
    bendRadius: string;
    leadLength: string;
    angle: string;
    category: string;
    unit: string;
    spacing: string;
    distFromFittings: string;
    distFromCoupling: string;
    distFromBeam: string;
    diameter: string;
    depth: string;
    width: string;
    errorHangerRodLength: string;
    errorHangerColor: string;
    rodDiameter: string;
    noOverride: false;
    itemType: string;
    isEditViewSettings: false;
    isHanger: string;
    isSysQueConnectOn: false;
    offsetType: string;
    bendType: string;
    defaultFor: string;
    position: string;
    specId: string;
    imageDescriptionType: ImageDescriptionType.IDS;
    itemPath: string;
    systemName: string;
    isFromEdit: false;
    isFromDuplicate: false;
    electricalType: string;
    isClearence: false;
    partType: string;
    isWall: false;
    connectorCount: number;
    serviceTypeColor: string;
    lineWeight: string;
    linePattern: string;
    length: string;
    serviceTypes: any[];
    supportDiscipline: string;
    parameters: any;
    oldMaterial: string;
    csvName: string;
  };
  private readonly closed$ = new Subject<void>();

  constructor(
    @Inject(RESULT_CONTAINER_FACADE_TOKEN) public resultContainerFacade: IResultContainerFacade,
    public sysqueStateService: SysQueStateService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
    this.subscribeToLocalStorageUpdates();
    this.subscribeToLastCurateSearchQuery();
    this.subscribeToSelectorChanges();

    this.resultContainerFacade.notifySelectorChange$.subscribe((data) => {
      this.selectedTab = data;
      if (data === GlobalConstants.SELECTED_TAB_CURATE_SEARCH) {
        this.resultContainerFacade.selectCurateSearchResult().subscribe((data) => {
          this.resultsFromCurate = data ?? [];
          if (this.resultsFromCurate.length > 100) {
            this.resultInformation = this.resultsFromCurate.slice(0, 100);
            this.resultsFromCurate.splice(0, 100);
          } else {
            this.resultInformation = [...this.resultInformation, ...this.resultsFromCurate];
            this.resultsFromCurate = [];
          }
        });
      }
      if (data === GlobalConstants.SELECTED_TAB_TREE_VIEW) {
        this.resultInformation = [];
      }
    });
  }

  ngOnDestroy(): void {
    if (this.resultInformationSub$) {
      this.resultInformationSub$.unsubscribe();
    }
    if (this.treeViewResultInformationSub$) {
      this.treeViewResultInformationSub$.unsubscribe();
    }
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
    this.closed$.next();
    this.closed$.complete();
  }

  subscribeToLocalStorageUpdates() {
    this.resultContainerFacade
      .getLocalStorageUpdate()
      .pipe(takeUntil(this.closed$))
      .subscribe((key) => {
        switch (key) {
          case 'resultLength':
            this.handleResultLengthUpdate();
            break;
          case 'resultState':
            this.handleResultStateUpdate();
            break;
        }
      });
  }

  handleResultLengthUpdate() {
    const resultLength = this.resultContainerFacade.getLocalStorageItem('resultLength');
    this.value = resultLength;
    this.visibility = resultLength !== '100';
  }

  handleResultStateUpdate() {
    const state = this.resultContainerFacade.getLocalStorageItem('resultState');
    this.visibility = state === 'true';
  }

  subscribeToLastCurateSearchQuery() {
    this.resultContainerFacade
      .selectLastCurateSearchQuery()
      .pipe(takeUntil(this.closed$))
      .subscribe((data) => (this.lastCurateSearchQuery = data));
  }

  subscribeToSelectorChanges() {
    this.resultContainerFacade.notifySelectorChange$.pipe(takeUntil(this.closed$)).subscribe(() => {
      this.resultContainerFacade.result$
        .pipe(takeUntil(this.closed$))
        .subscribe((data) => (this.resultInformation = data ?? []));
    });
  }

  getNext() {
    if (this.resultsFromCurate.length > 0) {
      this.resultInformation = this.resultInformation.concat(this.resultsFromCurate.slice(0, 100));
      this.resultsFromCurate.splice(0, 100);
      this.cd.detectChanges();
    }
  }

  itemClick(event: any, item: ResultInformation) {
    if (event.itemData.text === 'Sizes') {
      return;
    }

    console.log('Right click add to system was clicked');
    this.addFittingState = this.sysqueStateService.getElectricalState();
    let isCustomContent: string = '';

    const itemInfo: ResultItemInformation = {
      NameResult: item.NameResult,
      ImagePath: item.Image,
      ImageResult: '',
      Path: item.Path,
      FittingId: item.FittingId,
      FittingIdNumber: item.FittingIdNumber,
    };

    this.item.descriptionInstance = itemInfo.NameResult;
    this.item.id = itemInfo.FittingIdNumber;
    this.item.image = itemInfo.ImagePath;
    this.item.name = itemInfo.NameResult;

    this.addFittingState.categoryList.forEach((element) => {
      if (element.tabName === this.addFittingState.selectedCategory) {
        isCustomContent = element.isCustomContent;
      }
    });

    console.log('This is the system where we want to add a new item', this.addFittingState.selectedSystem);
    console.log('This is the category where we want to add a new item', this.addFittingState.selectedCategory);
    console.log('This is the item we want to add', itemInfo);
    console.log('Wheter the item is custom content or not', isCustomContent);

    this.resultContainerFacade.addToSystem(
      itemInfo,
      this.addFittingState.selectedSystem,
      this.addFittingState.selectedCategory,
      isCustomContent
    );
  }

  handleStorageEvent(event: StorageEvent): void {
    if (event.key != GlobalConstants.ADD_FITTING_INFO) {
      return;
    }

    if (event.newValue === '') {
      return;
    }

    let result = parseInt(JSON.parse(event.newValue));

    if (result !== 0) {
      this.getWarningMessage(result);
    }
    localStorage.removeItem(GlobalConstants.ADD_FITTING_INFO);
  }

  getWarningMessage(result: number) {
    this.resultContainerFacade.updateLocalStorage(GlobalConstants.ADD_TO_SYSTEM_ALERT, this.errorMap.get(result));
  }

  setItemProperties(): Item {
    const itemAdedd: Item = {
      descriptionInstance: this.item.descriptionInstance,
      descriptionType: this.item.descriptionType,
      id: this.item.id,
      isDefault: false,
      displayPosition: '',
      image: this.item.image,
      isDisabled: false,
      tooltipItems: [],
      name: this.item.name,
      fittingType: '',
      connectors: '',
      shape: '',
      lengthTooltip: '',
      manufacturer: '',
      sizeRange: '',
      serviceType: '',
      serviceTypeColor: '',
      bendRadius: '',
      leadLength: '',
      angle: '',
      category: '',
      unit: '',
      spacing: '',
      distFromFittings: '',
      distFromCoupling: '',
      distFromBeam: '',
      diameter: '',
      depth: '',
      width: '',
      errorHangerRodLength: '',
      errorHangerColor: '',
      rodDiameter: '',
      noOverride: false,
      itemType: '',
      isEditViewSettings: false,
      isHanger: '',
      isSysQueConnectOn: false,
      offsetType: '',
      bendType: '',
      defaultFor: '',
      position: '',
      specId: '',
      imageDescriptionType: ImageDescriptionType.INN,
      itemPath: '',
      centerRadius: '',
      height: '',
    };
    return itemAdedd;
  }

  showMoreBtn() {
    return this.resultsFromCurate.length > 100 && this.selectedTab === GlobalConstants.SELECTED_TAB_CURATE_SEARCH;
  }
}
